import { inject, Injectable } from '@angular/core';
import {
    MultiRegistrationConfig,
    MultiregistrationController,
    RegistrationsResponse,
    UIParticipantStatus,
    UIState,
} from '@azavista/participants/dist/event-app/multiregistration/multiregistration-controller';
import { ApiService } from '../api.service';
import { IAvailableContact } from '../interfaces';
import { ShoppingCartResultUI } from '@azavista/payments/lib/shopping-cart';
import { AppLoggerService, LogLevel } from '../app-logger.service';
import { getIsRequireTicket } from './utils';
import { firstValueFrom, from, Observable, shareReplay } from 'rxjs';
import { SharedService } from '../shared.service';
import { TRANSLATION_MAP } from 'src/app/enums';
import { EventDataService } from '../event-data.service';
import { NotificationsService } from '../notifications.service';
import { AppService } from 'src/app/app.service';
import { NotificationMessageType } from '../enums';

export type MultiRegistrationInitData = MultiRegistrationConfig & {
    cartResult: ShoppingCartResultUI;
};

export type MultiRegistrationConfigFormatted = {
    config: MultiRegistrationConfig;
    cartDataFn: () => ShoppingCartResultUI,
}

const formatMultiRegistrationConfig = (source: MultiRegistrationConfigFormatted): MultiRegistrationConfigFormatted => {
    const { cartDataFn, config,  } = source;

    // config = { ...config };
    if (config.multiregistration_type === 'group' && config.unassigned_tickets_allowed) {

    }

    return source;
};



@Injectable({
    providedIn: 'root',
})
export class MultiRegistrationService {
    notificationsSvc = inject(NotificationsService);
    appSvc = inject(AppService);
    controller?: MultiregistrationController | undefined;
    config?: MultiRegistrationConfig | undefined;
    configFormatted?: MultiRegistrationConfig | undefined;
    uiState?: UIState | undefined;
    multiRegistrationData?: RegistrationsResponse | undefined;

    _multiRegistrationData$Map: {
        [workflowId: string]: Observable<RegistrationsResponse>
    } = {};

    get cartData(): ShoppingCartResultUI | undefined {
        return this.controller?.['_cartData'] as ShoppingCartResultUI;
    }

    sharedSvc = inject(SharedService);
    eventDataSvc = inject(EventDataService);

    constructor(
        private apiSvc: ApiService,
        private logSvc: AppLoggerService,
    ) {}

    async initController(
        event_id: string,
        config: MultiRegistrationConfig,
        cartDataFn: () => ShoppingCartResultUI,
        stateHandlerFn?: (state: UIState) => void
    ) {
        if (this.config?.workflow_id === config.workflow_id && this.controller) {
            return;
        }
        const {multiRegistrationData: registrationData } = await this.initMultiRegistrationData(config);
        this.controller = new MultiregistrationController(event_id, config);
        this.controller.initialize(
            registrationData,
            getIsRequireTicket(config) ? cartDataFn() : null,
            this.eventDataSvc.eventDependencies
        );
        this.logSvc.log(LogLevel.log, 'Multi Registration Init', {
            controller: this.controller,
            registrationData, config,
            hasGlobalError: this.controller.hasGlobalErrors(),
            hasParticipantError: this.controller.hasParticipantErrors(),
        });
        this.subscribeToUiState(stateHandlerFn);
        return this;
    }

    async initMultiRegistrationData(
        config: MultiRegistrationConfig,
    ) {
        const getCurrentInstance = () => this as MultiRegistrationService & Required<Pick<MultiRegistrationService, 'config' | 'multiRegistrationData'>>;
        if (this.config?.workflow_id === config.workflow_id && this.multiRegistrationData) {
            return getCurrentInstance();
        }
        this.config = config;
        this.multiRegistrationData = await this.getMultiRegistrationData(config.workflow_id);
        return getCurrentInstance();
    }

    destroy() {
        if (this.config?.workflow_id) {
            delete this._multiRegistrationData$Map[this.config.workflow_id];
            this.config = undefined;
            this.controller = undefined;
            this.uiState = undefined;
            this.multiRegistrationData = undefined;
            this.logSvc.log(LogLevel.log, 'Multi Registration Destroyed');
        }
    }

    subscribeToUiState(stateHandlerFn: (state: UIState) => void | undefined) {
        const subscription = this.controller?.getUIState();
        subscription.subscribe(() => {
            const state: UIState = {...subscription.getState()};
            this.uiState = state;
            this.logSvc.groupCollapsed('Multi Registration State');
            this.logSvc.log(LogLevel.log, {
                state,
                controller: this.controller,
                hasGlobalError: this.controller.hasGlobalErrors(),
                hasParticipantError: this.controller.hasParticipantErrors(),
            });
            this.logSvc.groupEnd();
            stateHandlerFn?.(state);
        });
    }

    async submitMultiRegistration(): Promise<boolean> {
        if (this.controller && !this.hasError()) {
            try {
                const payload = this.controller.getSubmissionPayload();
                await this.apiSvc.updateMultiRegistration(
                    this.config.workflow_id,
                    payload
                );
                return true;
            } catch (error) {
                this.notificationsSvc.showNotification(NotificationMessageType.error, this.appSvc.getErrorText(error));
                return false;
            }
        }
        this.notificationsSvc.showNotification(NotificationMessageType.error, 'Cannot submit multi registration.');
        return false;
    }

    async getMultiRegistrationData(workflow_id: string): Promise<RegistrationsResponse> {
        this._multiRegistrationData$Map[workflow_id] ||= from(this.apiSvc.getMultiRegistration(workflow_id)).pipe(shareReplay());
        const registrationData = await firstValueFrom(this._multiRegistrationData$Map[workflow_id]);
        registrationData.scanned_registrations ||= [];
        return registrationData;
    }

    async getAvailableContacts(workflowId: string): Promise<IAvailableContact[]> {
        try {
            const result = await this.apiSvc.getMultiRegistrationAvailableContacts(workflowId);
            return result.contacts || [];
        } catch (error) {
            this.notificationsSvc.showNotification(NotificationMessageType.error, this.appSvc.getErrorText(error));
            return [];
        }
    }

    getAddonOptionsByParticipantTicket(participantId: string | null, ticketTypeId: string) {
        const result = this.controller?.getAddonOptionsByParticipantTicket(participantId, ticketTypeId);
        this.logSvc.log(LogLevel.log, 'Multi Registration - getAddonOptionsByParticipantTicket', {
            result,
            participantId,
            ticketTypeId,
        });
        return result;
    }

    hasError() {
        return this.controller?.hasGlobalErrors() || this.controller?.hasParticipantErrors();
    }

    getGlobalErrorMessage = (state: UIState = this.uiState): string => {
        switch (state.error?.code) {
            case 'TICKETS_EXCEEDED':
                const cartItems = this.cartData.condensedItems;
                const ticketCountRecords = [
                    state.main_participant,
                    ...state.subparticipants,
                ].reduce((acc, participant) => {
                    const ticketTypeId = participant.ticket_type_id;
                    if (ticketTypeId && participant.status !== UIParticipantStatus.CANCELLED) {
                        acc[ticketTypeId] = (acc[ticketTypeId] || 0) + 1;
                     }
                     return acc;
                }, {} as {[ticketTypeId: string]: number});
                const exceededTicket = cartItems.find(item => ticketCountRecords[item.product.id] > item.quantity);
                const productName = exceededTicket?.product.name;
                const purchasedQuantity = exceededTicket?.quantity || 0;
                const assignedQuantity = ticketCountRecords[exceededTicket?.product.id || ''] || 0;
                const quantityToAdd = assignedQuantity - purchasedQuantity;
                return exceededTicket ? this.sharedSvc.translate(TRANSLATION_MAP.MULTIREGISTRATION_ERROR_TICKETS_EXCEEDED, {
                    productName, purchasedQuantity, assignedQuantity, quantityToAdd
                }) : state.error?.message || '';
            default:
                return state.error?.message || '';
        }
    };
}
